import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import { abgelaufen, angemeldetBis, merkenVergessen } from "./speicher";
import { istRecht, type Recht } from "./rechte";

export interface Person {
  id: string;
  vorname: string;
  name: string;
  aufgabe: string;
  foto_pfad: string | null;
  aktiv: boolean;
  erste_anmeldung_am: string | null;
}

export type Sitzung =
  | { status: "laedt" }
  | { status: "ohne_datenbank" }
  | { status: "abgemeldet" }
  /** Server nicht erreichbar: angemeldet bleiben, „Keine Verbindung“ zeigen, beim Ereignis `online` neu laden. */
  | { status: "offline" }
  | { status: "kein_zugang"; email: string }
  | { status: "angemeldet"; session: Session; email: string; person: Person; rechte: ReadonlySet<Recht>; gruppen: readonly string[] };

interface SitzungKontext {
  sitzung: Sitzung;
  neuLaden: () => Promise<void>;
  abmelden: (ueberall?: boolean) => Promise<void>;
}

const Kontext = createContext<SitzungKontext | null>(null);

/** Netzfehler beim Laden der Sitzung? Dann bleibt die Person angemeldet (BAUPLAN-PAKETE.md, Abschnitt 4.8). Andere Fehler, zum
 *  Beispiel ein ungültiger Schlüssel, führen wie bisher zur Anmeldung. */
export function istNetzfehler(e: unknown, online = typeof navigator === "undefined" ? true : navigator.onLine): boolean {
  if (!online) return true;
  if (!e || typeof e !== "object") return false;
  const f = e as { name?: unknown; message?: unknown };
  // supabase-js: fehlgeschlagene Erneuerung des Schlüssels ohne Netz
  if (f.name === "AuthRetryableFetchError") return true;
  // fetch ohne Netz: Chrome „Failed to fetch“, Safari „Load failed“, Firefox „NetworkError …“
  return typeof f.message === "string" && /failed to fetch|load failed|networkerror|network request failed|fetch failed/i.test(f.message);
}

export const PERSON_SPALTEN = "id, vorname, name, aufgabe, foto_pfad, aktiv, erste_anmeldung_am";

async function laden(session: Session | null): Promise<Sitzung> {
  if (!supabase) return { status: "ohne_datenbank" };
  if (!session) return { status: "abgemeldet" };
  if (abgelaufen(angemeldetBis(), new Date())) {
    await supabase.auth.signOut({ scope: "local" });
    merkenVergessen();
    return { status: "abgemeldet" };
  }
  // Flugmodus: gleich „Keine Verbindung“, statt drei Wiederholungen von supabase-js abzuwarten (etwa 7 Sekunden)
  if (typeof navigator !== "undefined" && navigator.onLine === false) return { status: "offline" };
  const uid = session.user.id;
  const email = session.user.email ?? "";
  const [person, rechte, gruppen] = await Promise.all([
    supabase.from("app_personen").select(PERSON_SPALTEN).eq("id", uid).maybeSingle(),
    supabase.rpc("app_meine_rechte"),
    supabase.from("app_mitgliedschaften").select("gruppe").eq("person", uid),
  ]);
  if (person.error || rechte.error || gruppen.error) throw person.error ?? rechte.error ?? gruppen.error;
  if (!person.data || !person.data.aktiv) return { status: "kein_zugang", email };
  return {
    status: "angemeldet",
    session,
    email,
    person: person.data,
    rechte: new Set((rechte.data ?? []).filter(istRecht)),
    gruppen: (gruppen.data ?? []).map((g) => g.gruppe),
  };
}

export function SitzungAnbieter({ children }: { children: ReactNode }) {
  const [sitzung, setSitzung] = useState<Sitzung>(supabase ? { status: "laedt" } : { status: "ohne_datenbank" });

  const aktualisieren = useCallback(async (session: Session | null) => {
    try {
      setSitzung(await laden(session));
    } catch (e) {
      // Ohne Netz: angemeldet bleiben und „Keine Verbindung“ zeigen. Sonst wie bisher zur Anmeldung.
      setSitzung(istNetzfehler(e) ? { status: "offline" } : { status: "abgemeldet" });
    }
  }, []);

  /** Sitzung aus dem Speicher holen. Scheitert die Erneuerung des Schlüssels am Netz, bleibt sie im Speicher: offline statt abgemeldet. */
  const ausSpeicher = useCallback(async () => {
    if (!supabase) return;
    const { data, error } = await supabase.auth.getSession();
    if (!data.session && error && istNetzfehler(error)) setSitzung({ status: "offline" });
    else await aktualisieren(data.session);
  }, [aktualisieren]);

  useEffect(() => {
    if (!supabase) return;
    let aktiv = true;
    void supabase.auth.getSession().then(({ data, error }) => {
      if (!aktiv) return;
      if (!data.session && error && istNetzfehler(error)) setSitzung({ status: "offline" });
      else void aktualisieren(data.session);
    });
    const { data } = supabase.auth.onAuthStateChange((ereignis, session) => {
      if (ereignis === "SIGNED_IN" || ereignis === "SIGNED_OUT" || ereignis === "USER_UPDATED") {
        // Nicht im Callback auf Supabase warten (Empfehlung von supabase-js), deshalb entkoppelt
        setTimeout(() => void aktualisieren(session), 0);
      }
    });
    return () => {
      aktiv = false;
      data.subscription.unsubscribe();
    };
  }, [aktualisieren]);

  const neuLaden = ausSpeicher;

  // Offline: sobald das Netz zurück ist, neu laden
  useEffect(() => {
    if (sitzung.status !== "offline") return;
    const neu = () => void ausSpeicher();
    window.addEventListener("online", neu);
    return () => window.removeEventListener("online", neu);
  }, [sitzung.status, ausSpeicher]);

  const abmelden = useCallback(async (ueberall = false) => {
    if (!supabase) return;
    await supabase.auth.signOut({ scope: ueberall ? "global" : "local" });
    merkenVergessen();
    setSitzung({ status: "abgemeldet" });
  }, []);

  const wert = useMemo(() => ({ sitzung, neuLaden, abmelden }), [sitzung, neuLaden, abmelden]);
  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}

export function useSitzung(): SitzungKontext {
  const k = useContext(Kontext);
  if (!k) throw new Error("useSitzung nur innerhalb von SitzungAnbieter.");
  return k;
}

/** Nur in Seiten hinter der Anmeldung: die angemeldete Person mit Rechten. */
export function useAngemeldet() {
  const { sitzung } = useSitzung();
  if (sitzung.status !== "angemeldet") throw new Error("Nicht angemeldet.");
  return sitzung;
}

export function useRecht(recht: Recht): boolean {
  const { sitzung } = useSitzung();
  return sitzung.status === "angemeldet" && sitzung.rechte.has(recht);
}
